/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Formik, FormikProps } from "formik";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { useScrollToTopOnLoad } from "../../helpers/windowHelpers";
import { SubFormsDataTypes } from "../../models/forms";
import { programsActions } from "../../redux/actions/programsActions";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import { getCurrentProgram } from "../../redux/selectors";
import { getSubFormData } from "../../redux/selectors/programsSelectors";
import { FormNavigationPanel } from "../FormNavigationPanel";
import { SubformDataProvider } from "../SubformDataProvider";

export type SubFormNavigationProps = Readonly<{
  formRef: React.RefObject<FormikProps<any>>;
  onCancel?: () => void;
  onBackNavigation?: () => void;
  onForwardNavigation: () => void;
  onSaveFormToCrm: (subFormId: string) => void;
}>;

export type SubFormProps = Readonly<{
  children: React.ReactNode;
  validateConfig: any;
  initialValues: any;
  formId: string;
  subFormId: string;
}> &
  SubFormNavigationProps;

const removeNullValues = (values: SubFormsDataTypes, initial: any): any => {
  const newValues = {};

  for (const propertyKey in initial) {
    const value = values ? values[propertyKey] : null;
    newValues[propertyKey] = value !== null && value !== undefined ? value : initial[propertyKey];
  }

  return newValues;
};

export const SubForm = (props: SubFormProps) => {
  const {
    children,
    validateConfig,
    initialValues,
    formId,
    subFormId,
    formRef,
    onCancel,
    onBackNavigation,
    onForwardNavigation,
    onSaveFormToCrm,
  } = props;
  const validate = Yup.object(validateConfig);
  const dispatch = useDispatch();
  const currentProgram = useSelector(getCurrentProgram);
  const currentValues = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, formId, subFormId)
  );

  const move = (event: any) => {
    const form = event.target.form;
    const index = Array.prototype.indexOf.call(form, event.target) + (event.shiftKey ? -1 : 1);
    let nextElement = form.elements[index];
    if (nextElement.disabled) {
      nextElement = event.shiftKey ? form.elements[index - 1] : form.elements[index + 1];
    }
    nextElement.focus();
    event.preventDefault();
  };

  const ImplementMovementsWithKeyboard = useCallback((event: any) => {
    const isElementInForm =
      event.target.nodeName === "INPUT" ||
      event.target.nodeName === "TEXTAREA" ||
      event.target.nodeName === "SELECT" ||
      event.target.nodeName === "BUTTON" ||
      event.target.nodeName === "OPTION";

    if (event.key === "Tab" && isElementInForm) {
      move(event);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", ImplementMovementsWithKeyboard);
    return () => {
      document.removeEventListener("keydown", ImplementMovementsWithKeyboard);
    };
  }, [ImplementMovementsWithKeyboard]);

  useScrollToTopOnLoad();

  const storedValues = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, formId, subFormId)
  );

  const onSave = (values: any) => {
    dispatch(
      programsActions.updateSubFormData(currentProgram, {
        formId,
        subFormId,
        subForm: values,
      })
    );
    onSaveFormToCrm(subFormId);
  };
  return (
    <Formik
      initialValues={removeNullValues(storedValues, initialValues)}
      validationSchema={validate}
      onSubmit={() => {} /* Empty since it's handled by 'onSave' */}
      innerRef={formRef}
      validateOnMount={false}
      validateOnChange={false}
    >
      {() => (
        <SubformDataProvider formId={formId} subFormId={subFormId}>
          <Form>
            {children}
            <FormNavigationPanel
              onCancel={onCancel}
              onBackNavigation={onBackNavigation}
              onSave={onSave}
              onForwardNavigation={onForwardNavigation}
              currentValues={formRef?.current?.values ?? currentValues}
            />
          </Form>
        </SubformDataProvider>
      )}
    </Formik>
  );
};
